import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Input, Label, Spinner, Table } from "reactstrap";

import CatalogSidePanel from "./CatalogSidePanel";

const formatDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (_error) {
    return value;
  }
};

const formatMovementType = (value) =>
  String(value || "")
    .split("_")
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(" ");

function CatalogBarcodeScanPanel({
  isOpen,
  onClose,
  onResolve,
  resolution,
  isResolving,
  onPrint,
  onRestock,
  onAdjust,
  onOpenProduct,
}) {
  const inputRef = useRef(null);
  const [barcodeValue, setBarcodeValue] = useState("");

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select?.();
    }, 20);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setBarcodeValue("");
      return;
    }

    if (resolution?.barcode) {
      setBarcodeValue(resolution.barcode);
    }
  }, [isOpen, resolution]);

  const resultTitle = useMemo(() => {
    if (!resolution?.matched) {
      return null;
    }

    return resolution.variant?.title || resolution.product?.title || null;
  }, [resolution]);

  const canAdjust =
    resolution?.matched &&
    resolution?.product?.inventoryMode === "aggregate" &&
    resolution?.variant?.id;

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmed = barcodeValue.trim();
    if (!trimmed) {
      return;
    }

    onResolve?.(trimmed);
  };

  const handleScanAgain = () => {
    setBarcodeValue("");
    inputRef.current?.focus();
    inputRef.current?.select?.();
  };

  return (
    <CatalogSidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="Scan barcode"
      description="Focus the field, scan with a hardware barcode scanner, and jump straight into the real catalog variant context."
      footer={
        <>
          <Button color="secondary" outline onClick={onClose}>
            Close
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={isResolving || !barcodeValue.trim()}
          >
            {isResolving ? "Resolving…" : "Resolve barcode"}
          </Button>
        </>
      }
    >
      <form className="catalog-scan-panel__form" onSubmit={handleSubmit}>
        <div className="catalog-scan-panel__field">
          <Label for="catalog-barcode-scan-input">Scan barcode</Label>
          <Input
            id="catalog-barcode-scan-input"
            innerRef={inputRef}
            value={barcodeValue}
            onChange={(event) => setBarcodeValue(event.target.value)}
            placeholder="Scan here. Most scanners submit Enter automatically."
            autoComplete="off"
            spellCheck={false}
          />
        </div>
      </form>

      {isResolving ? (
        <div className="catalog-scan-panel__loading">
          <Spinner color="primary" size="sm" />
          <span>Looking up the barcode…</span>
        </div>
      ) : null}

      {!isResolving && resolution?.matched === false ? (
        <Alert color="warning" className="mb-0">
          {resolution?.ambiguous ? (
            <>
              {resolution.message || "This barcode is ambiguous in the catalog inventory data."}
            </>
          ) : (
            <>
              No catalog variant matched <code>{resolution.barcode}</code>. Try scanning again or check whether the label belongs to a legacy item that has not been migrated yet.
            </>
          )}
        </Alert>
      ) : null}

      {resolution?.matched ? (
        <div className="catalog-scan-panel__result">
          <div className="catalog-print-summary">
            <div>
              <span>Matched item</span>
              <strong>{resultTitle || "Resolved"}</strong>
            </div>
            <div>
              <span>Barcode source</span>
              <strong>
                {resolution.matchType === "inventory_unit_barcode"
                  ? "Inventory unit"
                  : "Catalog variant"}
              </strong>
            </div>
          </div>

          <div className="catalog-scan-panel__hero">
            <div className="catalog-scan-panel__hero-copy">
              <div className="catalog-studio-variant-title">
                {resolution.product?.title}
              </div>
              <div className="catalog-ops-muted">
                {resolution.variant?.title}
                {resolution.product?.brandName
                  ? ` · ${resolution.product.brandName}`
                  : ""}
              </div>
            </div>
            <div className="catalog-scan-panel__hero-badges">
              <Badge color="secondary" pill>
                {resolution.product?.inventoryMode === "serialized"
                  ? "Serialized"
                  : "Aggregate"}
              </Badge>
              {resolution.inventoryUnit ? (
                <Badge color="info" pill>
                  Unit barcode
                </Badge>
              ) : null}
            </div>
          </div>

          <div className="catalog-scan-panel__details">
            <div>
              <span>SKU</span>
              <strong>{resolution.variant?.sku || "—"}</strong>
            </div>
            <div>
              <span>Variant barcode</span>
              <strong>{resolution.variant?.barcode || "—"}</strong>
            </div>
            {resolution.inventoryUnit ? (
              <div>
                <span>Unit status</span>
                <strong>{formatMovementType(resolution.inventoryUnit.status)}</strong>
              </div>
            ) : null}
            {resolution.inventoryUnit?.locationName ? (
              <div>
                <span>Unit location</span>
                <strong>{resolution.inventoryUnit.locationName}</strong>
              </div>
            ) : null}
          </div>

          <div className="catalog-scan-panel__actions">
            <Button
              color="primary"
              size="sm"
              onClick={() => onRestock?.(resolution)}
            >
              Restock
            </Button>
            <Button
              color="secondary"
              size="sm"
              onClick={() => onAdjust?.(resolution)}
              disabled={!canAdjust}
            >
              Adjust stock
            </Button>
            <Button
              color="secondary"
              outline
              size="sm"
              onClick={() => onPrint?.(resolution)}
              disabled={!resolution.variant?.barcode}
            >
              Print labels
            </Button>
            <Button color="link" size="sm" onClick={() => onOpenProduct?.(resolution)}>
              Open product
            </Button>
            <Button
              color="link"
              size="sm"
              className="catalog-print-selection-button"
              onClick={handleScanAgain}
            >
              Scan again
            </Button>
          </div>

          {!canAdjust ? (
            <Alert color="secondary" className="mb-0">
              Stock correction currently supports aggregate inventory only. Serialized items can still be restocked, printed, and inspected from this scan result.
            </Alert>
          ) : null}

          <div className="catalog-scan-panel__stock">
            <h4>Stock snapshot</h4>
            {resolution.variant?.inventory?.aggregateByLocation?.length ? (
              <div className="catalog-studio-preview-table">
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>On hand</th>
                      <th>Reserved</th>
                      <th>Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resolution.variant.inventory.aggregateByLocation.map((entry) => (
                      <tr key={`${resolution.variant.id}-${entry.locationId}`}>
                        <td>{entry.locationName || "—"}</td>
                        <td>{entry.onHand}</td>
                        <td>{entry.reserved}</td>
                        <td>
                          <strong>{entry.available}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="catalog-print-summary">
                <div>
                  <span>Serialized available</span>
                  <strong>{resolution.variant?.inventory?.serialized?.available || 0}</strong>
                </div>
                <div>
                  <span>Total units</span>
                  <strong>{resolution.variant?.inventory?.serialized?.total || 0}</strong>
                </div>
              </div>
            )}
          </div>

          <div className="catalog-scan-panel__history">
            <h4>Recent movements</h4>
            {resolution.recentMovements?.length ? (
              <div className="catalog-studio-preview-table">
                <Table responsive>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Type</th>
                      <th>Location</th>
                      <th>Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resolution.recentMovements.map((entry) => (
                      <tr key={entry.id}>
                        <td>{formatDateTime(entry.createdAt)}</td>
                        <td>{formatMovementType(entry.movementType)}</td>
                        <td>{entry.locationName || "—"}</td>
                        <td>
                          <strong>{entry.quantityDelta > 0 ? `+${entry.quantityDelta}` : entry.quantityDelta}</strong>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <Alert color="secondary" className="mb-0">
                No movement history has been recorded for this variant yet.
              </Alert>
            )}
          </div>
        </div>
      ) : null}
    </CatalogSidePanel>
  );
}

export default CatalogBarcodeScanPanel;
